import React from "react";
import { cn } from "@/lib/utils";

/**
 * Badge — status/label chip primitive.
 *
 * Tones:
 *   neutral | accent | success | warning | info | danger
 *
 * Sizes: sm (default) | md
 *
 * Shapes:
 *   pill (default, `--ds-radius-pill`) — retained for consumer surfaces.
 *   sharp (`--ds-radius-input`, 3px) — sharp-edged executive chip for
 *     enterprise admin tables where pill-shape reads consumer-warm.
 *
 * Guardrails:
 *   • No filled saturated backgrounds — every tone uses `-soft` bg
 *     with strong-colored text for readable low-noise chips.
 *   • `shape="pill"` is the ONE component allowed to use
 *     `--ds-radius-pill`. `shape="sharp"` opts back into the crisp
 *     `--ds-radius-input` used by inputs and category chips.
 */

const TONES = {
  neutral: "bg-[var(--ds-color-surface-muted)] text-[var(--ds-color-ink-muted)] border-[var(--ds-color-border)]",
  accent:  "bg-[var(--ds-color-accent-soft)] text-[var(--ds-color-accent-strong)] border-[var(--ds-color-accent-soft)]",
  success: "bg-[var(--ds-color-success-soft)] text-[var(--ds-color-success)] border-[var(--ds-color-success-soft)]",
  warning: "bg-[var(--ds-color-warning-soft)] text-[var(--ds-color-warning)] border-[var(--ds-color-warning-soft)]",
  info:    "bg-[var(--ds-color-info-soft)]    text-[var(--ds-color-info)]    border-[var(--ds-color-info-soft)]",
  danger:  "bg-[var(--ds-color-danger-soft)]  text-[var(--ds-color-danger)]  border-[var(--ds-color-danger-soft)]",
  // Executive muted tones — desaturated variants for enterprise admin
  // pills. Preserves the soft-bg + strong-text guardrail; introduced in
  // Wave 5-Late (Beta anchor) after reviewer flagged the default
  // success/danger softs as consumer-warm (mint / pink).
  successMuted: "bg-[var(--ds-color-success-muted-soft)] text-[var(--ds-color-success-muted)] border-[var(--ds-color-admin-border)]",
  neutralMuted: "bg-[var(--ds-color-neutral-muted-soft)] text-[var(--ds-color-neutral-muted)] border-[var(--ds-color-admin-border)]",
};

const SIZES = {
  sm: "text-[11px] px-2 h-5",
  md: "text-[12px] px-2.5 h-6",
};

const SHAPES = {
  pill:  "rounded-[var(--ds-radius-pill)]",
  sharp: "rounded-[var(--ds-radius-input)]",
};

export function Badge({
  tone = "neutral",
  size = "sm",
  shape = "pill",
  className,
  "data-testid": testId,
  children,
  ...rest
}) {
  return (
    <span
      data-testid={testId}
      className={cn(
        "inline-flex items-center gap-1 border",
        "font-medium leading-none whitespace-nowrap",
        SHAPES[shape] || SHAPES.pill,
        TONES[tone] || TONES.neutral,
        SIZES[size] || SIZES.sm,
        className
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

export default Badge;
